import type { GetServerSideProps } from 'next';
import type { PageProps } from 'lib/queries/pages';
import type { Page } from 'types/page';
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PageWrap from 'components/page-wrap';
import Blocks from 'components/blocks';

import { getPageServerSide } from 'lib/queries/pages';
import { getPostHogServerClient } from 'lib/posthog-server';

import { usePostHog } from 'posthog-js/react';
import StickyMobileCta from 'components/sticky-mobile-cta';
import MedicalReviewer from 'components/medical-reviewer';
import MedicalWebPageJson from 'components/structured-data/medical-webpage';
import ServiceJson from 'components/structured-data/service';

const HeroCardNew = dynamic(() => import('components/hero-card-new'));
const Loader = dynamic(() => import('components/loader'));

const PageContent: React.FC<PageProps> = ({
  page: {
    title,
    seo,
    date,
    modified,
    slug,
    acf: {
      hero,
      name,
      affiliateLogo,
      affiliateWebsite,
      formUrl,
      pauseAppointments,
      redirectMedicalCard,
      redirectProductEndpoint,
      redirectCannabisCounseling,
      description,
      imageObjectCaption,
      blocks,
      pageType,
      stateProductImage,
      product,
      totalMedicalCardPrice,
      states,
      city,
      hasReferral,
      isClinic,
    },
  },
  settings: { seoSettings },
  variant,
  featureFlag,
  inPersonEvents,
}) => {
  const posthog = usePostHog();

  useEffect(() => {
    if (featureFlag) {
      /*       posthog.capture('$feature_flag_called', {
        $feature_flag: featureFlag,
        $feature_flag_response: variant,
      }); */
      posthog.capture('$pageview', {
        experiment_page: true,
      });
    }
  }, [featureFlag, variant]);
  return (
    <PageWrap seo={seo} pageType={pageType} title={title}>
      <MedicalWebPageJson
        seo={seo}
        date={date}
        title={title}
        modified={modified}
        stateName={slug.replace(/-/g, ' ').replace(/\b\w/g, function (letter) {
          return letter.toUpperCase();
        })}
        siteUrl={seoSettings.siteUrl}
        name={name}
        description={description}
        imageObjectCaption={imageObjectCaption}
        siteDescription={seoSettings.homepage.homepageDescription}
      />
      <ServiceJson
        seo={seo}
        product={product}
        totalMedicalCardPrice={totalMedicalCardPrice}
        stateName={slug.replace(/-/g, ' ').replace(/\b\w/g, function (letter) {
          return letter.toUpperCase();
        })}
      />
      <>
        <HeroCardNew
          hero={hero}
          states={states}
          affiliateLogo={affiliateLogo}
          pauseAppointments={pauseAppointments}
          redirectMedicalCard={redirectMedicalCard}
          inPersonEvents={inPersonEvents}
          showDoIQualify={false}
          addEmailInput={false}
          passwordless={true}
          priceTag={
            variant === 'test'
              ? { price: '99', comparePrice: '139', saveAmount: '40' }
              : null
          }
        />
        <StickyMobileCta
          observeTargetId="hero-cta-sentinel"
          stateCode={states?.[0]}
          formSubmitText={hero.formSubmitText || 'Get your card'}
        />
      </>
      {blocks && blocks.length > 0 ? (
        <Blocks
          blocks={blocks}
          pageType={pageType}
          states={states}
          modified={modified}
          stateProductImage={stateProductImage}
          hasReferral={hasReferral}
        />
      ) : null}
      <MedicalReviewer modified={modified} />
    </PageWrap>
  );
};

const Page: React.FC<PageProps> = props => {
  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  return <PageContent {...props} />;
};

export const getServerSideProps = (async context => {
  const { req } = context;

  const distinctId = req.headers['x-posthog-distinct-id'] as string;
  const flagKey = 'website-price-tag-MA';

  let variant: string | null = null;

  try {
    const posthog = getPostHogServerClient();
    const flags = await posthog.evaluateFlags(distinctId, {
      personProperties: { $pathname: '/medical-card/massachusetts' },
    });

    const flagValue = flags.getFlag(flagKey) as string | undefined;

    if (flagValue) {
      variant = flagValue;
    } else {
      console.warn(
        `[PostHog] Flag "${flagKey}" returned undefined for ${distinctId} — excluding from experiment`,
      );
    }
  } catch (error) {
    console.warn(
      `[PostHog] evaluateFlags failed for ${distinctId}:`,
      error?.message || error,
      '— excluding from experiment',
    );
  }

  return await getPageServerSide({
    ...context,
    variant: variant as 'test' | 'control' | null,
    featureFlag: variant ? flagKey : null,
    slug: 'medical-card/massachusetts',
    posthogBootstrap: {
      distinctID: distinctId,
      featureFlags: variant ? { [flagKey]: variant } : {},
    },
  });
}) satisfies GetServerSideProps<PageProps>;

export default Page;
